import { gradingApi, type SaveGradingPayload } from "@api/grading"
import { progressApi, type ProgressSheetParams, type SetProgressPayload } from "@api/progress"
import { classroomKeys } from "@features/classrooms"
import { homeKeys } from "@features/home"
import { materialKeys } from "@features/materials"
import { studentKeys } from "@features/students"
import { analytics } from "@lib/analytics"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { gradingKeys, progressKeys } from "./queryKeys"

import { useRealtimeEvent } from "@/realtime"
import type { ProgressUpload } from "@/types"

/**
 * Read one grading submission, polling-friendly while in flight.
 *
 *  - `placeholderData: (prev) => prev` keeps the previous payload
 *    visible across realtime phase events (no flicker).
 *  - `staleTime: 0` while in flight so realtime push events trigger
 *    a fresh read; the realtime channel is the primary signal but
 *    this hook is the cold-load + reconnect fallback (PRD-20 §FR-25).
 */
export const useGradingUpload = (uploadId: string | undefined) =>
  useQuery({
    queryKey: uploadId ? gradingKeys.upload(uploadId) : gradingKeys.upload("none"),
    queryFn: () => gradingApi.get(uploadId as string),
    enabled: !!uploadId,
    placeholderData: (prev) => prev,
    staleTime: 0,
  })

/**
 * Dispatch a new grading upload (1-3 images for a (task, student)
 * pair). On success the returned `ProgressUpload` is primed into the
 * cache under its detail key so the modal can render the
 * `pending_grading` state immediately without a follow-up fetch.
 */
export const useCreateGradingUpload = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: gradingApi.createUpload,
    onSuccess: (upload: ProgressUpload, payload) => {
      // Counts and a flag only. `payload.student_id` and the image files are
      // exactly what PRD-57 FR-6 forbids.
      analytics.track("grading_upload_created", {
        image_count: payload.files.length,
        regrade: payload.force === true,
      })
      qc.setQueryData(gradingKeys.upload(upload.id), upload)
    },
  })
}

/**
 * Persist the professor's confirmed grade. Backend writes
 * `ProgressRecord.grade` + `audit_log(grade.scored_from_photo)`.
 *
 * PRD-20 v1.3 §FR-51: the typical success flow is now auto-commit inside
 * the Celery task (no modal click needed). This mutation is still used by
 * the manual-override path — when a professor edits an auto-committed
 * grade via the regular cell editor with `accept_path="manual_override"`.
 *
 * Cache: refreshes the upload row in place and invalidates every consumer
 * surface (`progress`, `students`, `materials`, `classrooms`, `home`) so
 * the override propagates without a manual refetch.
 */
export const useSaveGrading = (uploadId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: SaveGradingPayload) => gradingApi.save(uploadId, payload),
    onSuccess: (upload: ProgressUpload, payload) => {
      // Fourth `grade_saved` source. `accept_path` separates "the OCR was
      // right, I confirmed it" from "I overrode it" — the number that says
      // whether photo grading can be trusted. Never `final_score`.
      analytics.track("grade_saved", {
        source: "photo_grading",
        accept_path: payload.accept_path,
        task_id: upload.task_id,
      })
      qc.setQueryData(gradingKeys.upload(upload.id), upload)
      void qc.invalidateQueries({ queryKey: progressKeys.all })
      void qc.invalidateQueries({ queryKey: studentKeys.all })
      void qc.invalidateQueries({ queryKey: materialKeys.all })
      void qc.invalidateQueries({ queryKey: classroomKeys.all })
      void qc.invalidateQueries({ queryKey: homeKeys.all })
    },
  })
}

/**
 * PRD-20 v1.4 §FR-66 — "Quitar envío" from the cell / drawer / student page.
 *
 * Hard-deletes a non-terminal `ProgressUpload`. Backend refuses GRADED
 * uploads with 422 `upload_already_graded` — graded grades must go
 * through the cell editor's clear-grade flow, not delete-upload.
 *
 * Invalidates the same five branches as `useSaveGrading` so every
 * consumer surface re-renders the freed cell immediately.
 */
export const useDeletePendingUpload = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uploadId: string) => gradingApi.deleteUpload(uploadId),
    onSuccess: (_data, uploadId) => {
      analytics.track("grading_upload_deleted")
      qc.removeQueries({ queryKey: gradingKeys.upload(uploadId) })
      void qc.invalidateQueries({ queryKey: progressKeys.all })
      void qc.invalidateQueries({ queryKey: studentKeys.all })
      void qc.invalidateQueries({ queryKey: materialKeys.all })
      void qc.invalidateQueries({ queryKey: classroomKeys.all })
      void qc.invalidateQueries({ queryKey: homeKeys.all })
    },
  })
}

/**
 * PRD-20 v1.4 §FR-66 — fetch signed URLs for each image in a grading
 * upload, on demand. Only enabled when an `uploadId` is provided AND
 * the caller wants to render the lightbox (`enabled` flag).
 *
 * Cache key includes the upload id so the per-upload list is reusable
 * across the cell / drawer / student-page surfaces without refetching
 * inside a single SPA session.
 */
export const useUploadPreview = (uploadId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: gradingKeys.upload(uploadId ?? "none").concat(["preview"]),
    queryFn: () => gradingApi.listFiles(uploadId as string),
    enabled: !!uploadId && enabled,
    staleTime: 60 * 1000, // signed URLs are valid for 5 min; 1 min cache is safe
  })

/**
 * Global cross-surface invalidator for the auto-commit path.
 *
 * PRD-20 v1.3 §FR-49 — mounted once at the SPA root (AppShell). When a
 * grading TaskRun completes (worker auto-commits the score via PRD-20
 * v1.3 §FR-47), every consumer surface reading from these branches
 * refreshes automatically:
 *
 *   • the material results rows (PRD-58)      — `progressKeys.all`
 *   • Student detail (Tareas, chart, KPIs)     — `studentKeys.all`
 *   • /materiales/:id (per-student grid)       — `materialKeys.all`
 *   • Roster pill + classroom stats            — `classroomKeys.all`
 *   • Home dashboard KPIs                      — `homeKeys.all`
 *
 * Failure events are ignored — the failure UI is in the modal itself
 * and no ProgressRecord was touched.
 */
export const useGradingCompletionInvalidator = () => {
  const qc = useQueryClient()
  useRealtimeEvent("task.progress", (payload) => {
    if (payload.kind !== "grading" || payload.status !== "completed") return
    // The upload row itself, so surfaces bound to it (the scan page's review
    // screen, the modal) see PENDING_REVIEW + ocr_metadata instead of the
    // pre-grading snapshot primed at POST time. Without this nothing ever
    // refetches the row and the review screen is unreachable — the user gets
    // told the grade was saved when the strict gate actually deferred it.
    if (payload.payload_ref_kind === "progress_upload" && payload.payload_ref_id) {
      void qc.invalidateQueries({ queryKey: gradingKeys.upload(payload.payload_ref_id) })
    }
    void qc.invalidateQueries({ queryKey: progressKeys.all })
    void qc.invalidateQueries({ queryKey: studentKeys.all })
    void qc.invalidateQueries({ queryKey: materialKeys.all })
    void qc.invalidateQueries({ queryKey: classroomKeys.all })
    void qc.invalidateQueries({ queryKey: homeKeys.all })
  })
}

/**
 * The classroom's student × task sheet. Its one consumer since PRD-58 is
 * the scan-to-grade picker (PRD-36), which needs the roster + gradeable
 * tasks of a classroom in one request.
 */
export const useProgressSheet = (params: ProgressSheetParams) =>
  useQuery({
    queryKey: progressKeys.sheet(params),
    queryFn: () => progressApi.sheet(params),
    enabled: !!params.classroom_id,
    placeholderData: (prev) => prev,
  })

/**
 * PRD-58 §FR-2.4 — the one manual-grade write: the inline grade field on
 * a material's results rows. `PUT /progress/student/{sid}/task/{tid}`
 * upserts the ProgressRecord and audits `progress.updated`.
 */
export const useSetProgressCell = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      studentId,
      taskId,
      payload,
    }: {
      studentId: string
      taskId: string
      payload: SetProgressPayload
    }) => progressApi.setCell(studentId, taskId, payload),
    onSuccess: (_result, { taskId, payload }) => {
      // `!= null` on purpose: clearing a grade sends `null`, marking a
      // delivery without one sends `undefined` — neither is a grade saved.
      if (payload.grade != null) {
        analytics.track("grade_saved", { source: "material_results", task_id: taskId })
      }
      void qc.invalidateQueries({ queryKey: progressKeys.all })
      void qc.invalidateQueries({ queryKey: studentKeys.all })
      void qc.invalidateQueries({ queryKey: materialKeys.all })
      void qc.invalidateQueries({ queryKey: classroomKeys.all })
      void qc.invalidateQueries({ queryKey: homeKeys.all })
    },
  })
}
